/* ObiHub OS — üst çalışma sekmeleri (OS-2A).  KONTROLLÜ bileşenler: durum makinesini/router'ı bilmezler; olayları geri çağırımlarla bildirirler.
   ARIA: tablist/tab, aria-selected, roving tabIndex; manuel aktivasyon (oklar odağı taşır, Enter/Space etkinleştirir); Delete kapatma talebi üretir
   (kirli sekme kararı durum makinesindedir); taşan sekmeler "Diğer (n)" menüsünde; dar ekranda tek aktif sekme + "Açık işler (n)".
   Üretim sayfalarına BAĞLI DEĞİLDİR (uygulama koduna, yönlendiriciye ve dış ikon kütüphanelerine bağımlılığı yoktur). */
import React, { useEffect, useId, useRef, useState } from "react";
import { OsButton } from "../primitives";
import { computeLayout } from "./reducer";
import "./workspace.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");

const PinIcon = () => (
  <svg className="os-wtab__pin" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M9 3h6l-1 6 3 3H7l3-3-1-6zM12 12v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function OsWorkspaceTab({ tab, focusable, onActivate, onCloseRequest, onKeyDown, onFocus }) {
  return (
    <div className="os-wtab" data-active={tab.active ? "true" : "false"} data-dirty={tab.dirty ? "true" : "false"} data-pinned={tab.pinned ? "true" : "false"}>
      <button
        type="button"
        role="tab"
        aria-selected={tab.active ? "true" : "false"}
        tabIndex={focusable ? 0 : -1}
        data-id={tab.id}
        data-active={tab.active ? "true" : "false"}
        data-pinned={tab.pinned ? "true" : "false"}
        className="os-wtab__tab"
        onClick={() => onActivate(tab.id)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
      >
        {tab.active ? <span className="os-wtab__active-mark" aria-hidden="true" /> : null}
        {tab.pinned ? <PinIcon /> : null}
        <span className="os-wtab__label">{tab.label}</span>
        {tab.dirty ? <><span className="os-wtab__dirty" aria-hidden="true">●</span><span className="os-visually-hidden">Kaydedilmemiş değişiklik</span></> : null}
        {tab.pinned ? <span className="os-visually-hidden">Sabit</span> : null}
      </button>
      {tab.closeable ? (
        <button type="button" className="os-wtab__close" tabIndex={-1} aria-label={`Kapat: ${tab.label}`} onClick={() => onCloseRequest(tab.id)}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>
      ) : null}
    </div>
  );
}

export function OsWorkspaceOverflow({ items, label, onActivate, onCloseRequest, className }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const first = menuRef.current && menuRef.current.querySelector('[role="menuitem"]');
    if (first) first.focus();
    const onOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && triggerRef.current && !triggerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  if (!items || items.length === 0) return null;

  const openMenu = () => setOpen(true);
  const closeMenu = (restoreFocus) => {
    setOpen(false);
    if (restoreFocus && triggerRef.current) triggerRef.current.focus();
  };
  const onTriggerKeyDown = (event) => {
    if (["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      openMenu();
    }
  };
  const requestClose = (item) => {
    closeMenu(true);                                 // menü ÖNCE kapanır, odak tetikleyiciye döner; onay açılırsa odağı onay alır
    onCloseRequest(item.id);
  };
  const onItemKeyDown = (event, item, kind) => {
    const els = [...menuRef.current.querySelectorAll('[role="menuitem"]')];
    const index = els.indexOf(event.currentTarget);
    const go = (i) => { event.preventDefault(); els[(i + els.length) % els.length].focus(); };
    if (event.key === "ArrowDown") go(index + 1);
    else if (event.key === "ArrowUp") go(index - 1);
    else if (event.key === "Home") go(0);
    else if (event.key === "End") go(els.length - 1);
    else if (event.key === "Escape") { event.preventDefault(); closeMenu(true); }
    else if (event.key === "Tab") setOpen(false);
    else if (event.key === "Delete") { event.preventDefault(); if (item.closeable) requestClose(item); }
    else if (kind !== "close" && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onActivate(item.id); closeMenu(false); }
  };

  return (
    <div className="os-wtabs__overflow">
      <button
        ref={triggerRef}
        type="button"
        className={cx("os-wtabs__overflow-trigger", className)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        {label}
      </button>
      {open ? (
        <div ref={menuRef} id={menuId} role="menu" aria-label={label} className="os-wtabs__menu">
          {items.map((item) => (
            <div key={item.id} role="none" className="os-wtabs__menurow">
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                data-active={item.active ? "true" : "false"}
                className="os-wtabs__menuitem"
                onClick={() => { onActivate(item.id); closeMenu(false); }}
                onKeyDown={(event) => onItemKeyDown(event, item, "activate")}
              >
                <span className="os-wtabs__menuitem-label">{item.label}</span>
                {item.active ? <span className="os-visually-hidden">Etkin</span> : null}
                {item.dirty ? <><span className="os-wtab__dirty" aria-hidden="true">●</span><span className="os-visually-hidden">Kaydedilmemiş değişiklik</span></> : null}
              </button>
              {item.closeable ? (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className="os-wtabs__menuclose"
                  aria-label={`Kapat: ${item.label}`}
                  onClick={() => requestClose(item)}
                  onKeyDown={(event) => onItemKeyDown(event, item, "close")}
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ConfirmPanel({ label, onConfirmClose, onCancelClose, restoreFallback }) {
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const fallbackRef = useRef(restoreFallback);
  fallbackRef.current = restoreFallback;

  useEffect(() => {
    const previous = document.activeElement;         // açılıştan önceki anlamlı kontrol
    if (cancelRef.current) cancelRef.current.focus();
    let pulling = false;
    const onFocusIn = (event) => {                   // odak modal dışına kaçarsa geri çekilir (inert desteklemeyen ortamlar dahil)
      const dialog = dialogRef.current;
      const cancel = cancelRef.current;
      if (pulling || !dialog || !cancel || !cancel.isConnected || dialog.contains(event.target)) return;
      pulling = true;
      try { cancel.focus(); } finally { pulling = false; }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      const target = previous && previous.isConnected && previous !== document.body ? previous : (fallbackRef.current && fallbackRef.current());
      if (target && target.isConnected) target.focus();
    };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === "Escape") { event.preventDefault(); onCancelClose(); return; }
    if (event.key !== "Tab") return;
    const els = [...dialogRef.current.querySelectorAll("button")];
    if (els.length === 0) return;
    const index = els.indexOf(document.activeElement);
    event.preventDefault();                          // odak yalnız iki eylem arasında döner
    const next = event.shiftKey ? (index <= 0 ? els.length - 1 : index - 1) : (index === -1 || index === els.length - 1 ? 0 : index + 1);
    els[next].focus();
  };

  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="os-wtabs__confirm"
      onKeyDown={onKeyDown}
    >
      <p id={titleId} className="os-wtabs__confirm-title">Kaydedilmemiş değişiklikler var</p>
      <p id={bodyId} className="os-wtabs__confirm-body">“{label}” kapatılırsa bu değişiklikler kaybolur.</p>
      <div className="os-wtabs__confirm-actions">
        <OsButton ref={cancelRef} variant="secondary" size="sm" onClick={onCancelClose}>Vazgeç</OsButton>
        <OsButton variant="danger" size="sm" onClick={onConfirmClose}>Yine de kapat</OsButton>
      </div>
    </div>
  );
}

export function OsWorkspaceTabs({ tabs, activeId, capacity = 8, pendingCloseId = null, onActivate, onCloseRequest, onConfirmClose, onCancelClose, forceCompact = false }) {
  const unique = [];
  const seen = new Set();
  for (const t of tabs) if (!seen.has(t.id)) { seen.add(t.id); unique.push(t); }        // aynı kayıt iki kez görünmez
  const withActive = unique.map((t) => ({ ...t, active: t.id === activeId }));
  const { visible, overflow } = computeLayout(withActive, capacity);
  const [focusId, setFocusId] = useState(null);
  const visibleIds = visible.map((t) => t.id);
  const rovingId = focusId && visibleIds.includes(focusId) ? focusId : (visibleIds.includes(activeId) ? activeId : visibleIds[0]);
  const activeTab = withActive.find((t) => t.active) || null;
  const pending = pendingCloseId ? withActive.find((t) => t.id === pendingCloseId) : null;
  const rootRef = useRef(null);
  const modal = !!pending;                           // modal açıkken arka plan etkileşimi tamamen engellenir
  const guard = (fn) => (...args) => { if (!modal) fn(...args); };
  const activate = guard(onActivate);
  const requestClose = guard(onCloseRequest);
  const restoreFallback = () => (rootRef.current ? rootRef.current.querySelector('[role="tab"][tabindex="0"]') : null);

  const onTabKeyDown = (event, tab) => {
    if (modal) { event.preventDefault(); return; }
    const els = [...event.currentTarget.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    const index = els.indexOf(event.currentTarget);
    const move = (i) => { event.preventDefault(); const target = els[(i + els.length) % els.length]; target.focus(); setFocusId(target.getAttribute("data-id")); };
    if (event.key === "ArrowRight") move(index + 1);
    else if (event.key === "ArrowLeft") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(els.length - 1);
    else if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onActivate(tab.id); }
    else if (event.key === "Delete" && tab.closeable) { event.preventDefault(); onCloseRequest(tab.id); }
  };

  return (
    <div ref={rootRef} className={cx("os-wtabs", forceCompact && "os-wtabs--compact")} data-os-root="">
      <div className="os-wtabs__bar" aria-hidden={modal ? "true" : undefined} inert={modal ? true : undefined}>
        <div role="tablist" aria-label="Açık çalışma sekmeleri" aria-orientation="horizontal" className="os-wtabs__list">
          {visible.map((tab) => (
            <OsWorkspaceTab
              key={tab.id}
              tab={tab}
              focusable={tab.id === rovingId}
              onActivate={activate}
              onCloseRequest={requestClose}
              onKeyDown={(event) => onTabKeyDown(event, tab)}
              onFocus={() => setFocusId(tab.id)}
            />
          ))}
        </div>
        <OsWorkspaceOverflow items={overflow} label={`Diğer (${overflow.length})`} onActivate={activate} onCloseRequest={requestClose} />
      </div>
      <div className="os-wtabs__compact" aria-hidden={modal ? "true" : undefined} inert={modal ? true : undefined}>
        <span className="os-wtabs__compact-active">{activeTab ? activeTab.label : "Açık iş yok"}</span>
        <OsWorkspaceOverflow items={withActive} label={`Açık işler (${withActive.length})`} onActivate={activate} onCloseRequest={requestClose} />
      </div>
      {pending ? <ConfirmPanel label={pending.label} onConfirmClose={onConfirmClose} onCancelClose={onCancelClose} restoreFallback={restoreFallback} /> : null}
    </div>
  );
}
